import type { AssetTypeCode, WorkflowJsonObject, WorkspaceCode } from '@atlas/types'
import { CardShell } from '@atlas/ui-components'
import { Eyebrow } from '@/components/editorial/PageHeader'
import { movedToIndex } from '@/domain/reorder'
import type { BuilderEdge, BuilderNode } from '@/domain/workflow-builder-layout'
import type {
	useCreateWorkflowTransition,
	useDeleteWorkflowStatus,
	useDeleteWorkflowTransition,
	useReorderWorkflowStatuses,
	useUpdateWorkflowStatus,
	useUpdateWorkflowTransition,
} from '@/hooks/useWorkflowAdmin'
import {
	useCreateWorkspaceAssignment,
	useDeleteWorkspaceAssignment,
	useUpdateWorkspaceAssignment,
	useWorkspaceAssignments,
} from '@/hooks/useWorkspaceAssignmentAdmin'
import { notifyError } from './notifyError'
import { TransitionInspectorPanel } from './TransitionInspectorPanel'
import { type StatusInspectorValue, WorkflowStatusInspector } from './WorkflowStatusInspector'
import { WorkspaceAssignmentSection } from './WorkspaceAssignmentSection'

function EmptyInspector({ statusCount, transitionCount }: { statusCount: number; transitionCount: number }) {
	return (
		<div>
			<Eyebrow>Inspector</Eyebrow>
			<p className="mt-2.5 text-ds-xs leading-relaxed text-ds-text-muted">
				Select a status node to edit its code, phase and default workspace. Select a transition line to edit its CTA and
				its pre / post hooks.
			</p>
			<p className="mt-3.5 border-t border-ds-border pt-3 font-mono text-ds-4xs leading-relaxed text-ds-text-subtle">
				{statusCount} statuses · {transitionCount} transitions
			</p>
		</div>
	)
}

/**
 * Bridges a selected status to `WorkspaceAssignmentSection` — fetches its
 * `workspace_assignment` rows and translates the section's edit callbacks
 * into the real mutations.
 */
function StatusWorkspacePanel({
	statusId,
	statusCode,
	workflowId,
}: {
	statusId: string
	statusCode: string
	workflowId: string
}) {
	const { data: assignments = [] } = useWorkspaceAssignments(statusId)
	const create = useCreateWorkspaceAssignment()
	const update = useUpdateWorkspaceAssignment()
	const remove = useDeleteWorkspaceAssignment()

	return (
		<WorkspaceAssignmentSection
			assignments={assignments}
			statusCode={statusCode}
			onSetDefault={(code: WorkspaceCode) =>
				create.mutate(
					{ workflowId, statusId, assetTypeCode: null, workspaceCode: code },
					{ onError: notifyError('Could not set the default workspace') },
				)
			}
			onAddOverride={(assetTypeCode: string, code: WorkspaceCode, onError) =>
				create.mutate(
					{ workflowId, statusId, assetTypeCode: assetTypeCode as AssetTypeCode, workspaceCode: code },
					{
						onError: (err) => {
							onError?.()
							notifyError('Could not add that override')(err)
						},
					},
				)
			}
			onUpdateOverride={(assignmentId: string, code: WorkspaceCode) =>
				update.mutate(
					{ assignmentId, statusId, body: { workspaceCode: code } },
					{ onError: notifyError('Could not save that override') },
				)
			}
			onRemoveOverride={(assignmentId: string) =>
				remove.mutate({ assignmentId, statusId }, { onError: notifyError('Could not remove that override') })
			}
			onUpdateOptions={(assignmentId: string, options: WorkflowJsonObject) =>
				update.mutate(
					{ assignmentId, statusId, body: { options } },
					{ onError: notifyError('Could not save the section list') },
				)
			}
		/>
	)
}

/** The status branch of the inspector: the status form plus that status's workspace assignments. */
function StatusPanel({
	node,
	activeWorkflowId,
	orderedStatusIds,
	updateStatus,
	deleteStatus,
	reorderStatuses,
	onClearSelection,
}: {
	node: BuilderNode
	activeWorkflowId: string | null
	orderedStatusIds: readonly string[]
	updateStatus: ReturnType<typeof useUpdateWorkflowStatus>
	deleteStatus: ReturnType<typeof useDeleteWorkflowStatus>
	reorderStatuses: ReturnType<typeof useReorderWorkflowStatuses>
	onClearSelection: () => void
}) {
	return (
		<>
			<WorkflowStatusInspector
				value={node as StatusInspectorValue}
				sequence={{ index: orderedStatusIds.indexOf(node.id), total: orderedStatusIds.length }}
				onMoveTo={(index) =>
					reorderStatuses.mutate(
						{
							workflowId: activeWorkflowId as string,
							statusIds: movedToIndex(orderedStatusIds, node.id, index),
						},
						{ onError: notifyError('Could not reorder the statuses') },
					)
				}
				isMoving={reorderStatuses.isPending}
				onChange={(patch, onError) =>
					updateStatus.mutate(
						{ statusId: node.id, body: patch, workflowId: activeWorkflowId as string },
						{
							onError: (err) => {
								onError?.()
								notifyError('Could not save that change')(err)
							},
						},
					)
				}
				onDelete={() =>
					deleteStatus.mutate(
						{ statusId: node.id, workflowId: activeWorkflowId as string },
						{ onSuccess: onClearSelection, onError: notifyError('Could not delete this status') },
					)
				}
				isDeleting={deleteStatus.isPending}
			/>
			{activeWorkflowId ? (
				<StatusWorkspacePanel statusId={node.id} statusCode={node.code} workflowId={activeWorkflowId} />
			) : null}
		</>
	)
}

/** The right-hand inspector — a status form, a transition form, or the empty-state hint, depending on the current selection. */
export function BuilderInspector({
	selectedNode,
	selectedEdge,
	dir,
	nodesById,
	statusCount,
	transitionCount,
	activeWorkflowId,
	templates,
	updateStatus,
	deleteStatus,
	reorderStatuses,
	orderedStatusIds,
	updateTransition,
	createTransition,
	deleteTransition,
	onSwitchDir,
	onClearSelection,
}: {
	selectedNode: BuilderNode | null
	selectedEdge: BuilderEdge | null
	dir: 'forward' | 'backward'
	nodesById: Map<string, BuilderNode>
	statusCount: number
	transitionCount: number
	activeWorkflowId: string | null
	templates: readonly { code: string; label: string }[]
	updateStatus: ReturnType<typeof useUpdateWorkflowStatus>
	deleteStatus: ReturnType<typeof useDeleteWorkflowStatus>
	reorderStatuses: ReturnType<typeof useReorderWorkflowStatuses>
	/** The workflow's active statuses in sequence order - the list a move submits, reordered by one place. */
	orderedStatusIds: readonly string[]
	updateTransition: ReturnType<typeof useUpdateWorkflowTransition>
	createTransition: ReturnType<typeof useCreateWorkflowTransition>
	deleteTransition: ReturnType<typeof useDeleteWorkflowTransition>
	onSwitchDir: (dir: 'forward' | 'backward') => void
	onClearSelection: () => void
}) {
	return (
		<CardShell as="aside" className="max-h-160 w-88 flex-none overflow-auto p-4">
			{selectedNode ? (
				<StatusPanel
					key={selectedNode.id}
					node={selectedNode}
					activeWorkflowId={activeWorkflowId}
					orderedStatusIds={orderedStatusIds}
					updateStatus={updateStatus}
					deleteStatus={deleteStatus}
					reorderStatuses={reorderStatuses}
					onClearSelection={onClearSelection}
				/>
			) : selectedEdge ? (
				<TransitionInspectorPanel
					key={`${selectedEdge.key}-${dir}`}
					edge={selectedEdge}
					dir={dir}
					fromLabel={nodesById.get(selectedEdge.fromId)?.label ?? selectedEdge.fromId}
					toLabel={nodesById.get(selectedEdge.toId)?.label ?? selectedEdge.toId}
					fromPosition={nodesById.get(selectedEdge.fromId)?.position ?? 0}
					toPosition={nodesById.get(selectedEdge.toId)?.position ?? 0}
					activeWorkflowId={activeWorkflowId}
					templates={templates}
					updateTransition={updateTransition}
					createTransition={createTransition}
					deleteTransition={deleteTransition}
					onSwitchDir={onSwitchDir}
					onDeleted={onClearSelection}
				/>
			) : (
				<EmptyInspector statusCount={statusCount} transitionCount={transitionCount} />
			)}
		</CardShell>
	)
}
